'use client'

import { ApiError } from '@/lib/api-client'
import { useCallback, useEffect, useRef } from 'react'
import { authService, type MeResponse } from '../services/auth-service'
import { useAuthStore } from '../stores/auth-store'
import type { AuthUser } from '../types'

/**
 * Hidrata el estado de auth desde la API propia y lo mantiene sincronizado.
 *
 * Se coloca una vez en el layout raíz via AuthProvider.
 *
 * Flujo:
 *  - Al montar → GET /api/auth/me (cookie httpOnly) → hidrata store + membresía
 *  - En `focus` de window → re-chequea la sesión
 *  - 401 → intenta POST /api/auth/refresh una vez antes de limpiar el estado
 */
export function useAuthListener() {
	const { setUser, clearAuth, setLoading, setOnboardingCompleted } = useAuthStore()
	const isSyncing = useRef(false)

	const applyMe = useCallback(
		(me: MeResponse) => {
			const user: AuthUser = {
				id: me.id,
				email: me.email,
				role: me.role,
				profile: {
					firstName: me.firstName,
					lastName: me.lastName,
					profileName: me.profileName,
					avatarUrl: me.avatarUrl ?? undefined,
				},
				avatarUrl: me.avatarUrl,
				createdAt: me.createdAt,
			}
			setUser(user)
			// Membresía: el backend devuelve companyId (empresa primaria) e isOnboarded
			setOnboardingCompleted(me.isOnboarded || !!me.companyId)
		},
		[setUser, setOnboardingCompleted]
	)

	const syncSession = useCallback(async () => {
		// Evitar llamadas simultáneas
		if (isSyncing.current) {
			return
		}
		isSyncing.current = true

		try {
			const me = await authService.getMe()
			applyMe(me)
		} catch (err) {
			if (err instanceof ApiError && err.statusCode === 401) {
				// Sesión expirada → intentar renovar cookies una vez
				const refreshed = await fetch('/api/auth/refresh', { method: 'POST' }).catch(() => null)
				if (refreshed?.ok) {
					try {
						const me = await authService.getMe()
						applyMe(me)
						return
					} catch {
						// Cae al clearAuth de abajo
					}
				}
				clearAuth()
			} else {
				// Error de red u otro fallo transitorio: conservar el estado actual
				setLoading(false)
			}
		} finally {
			isSyncing.current = false
		}
	}, [applyMe, clearAuth, setLoading])

	useEffect(() => {
		setLoading(true)
		void syncSession()

		const onFocus = () => {
			void syncSession()
		}
		window.addEventListener('focus', onFocus)

		return () => {
			window.removeEventListener('focus', onFocus)
		}
	}, [syncSession, setLoading])
}
